// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleCompositionVO, ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { CodingArticleUtils } from '@article/util/coding-article.utils';
import { ProjectModel } from '@project/model/project.model';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ArrayUtils } from '@shared/util/array-utils';
import { ConnectionAreaUsageVO, MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { CodingArticleProcessorDelegator } from './coding-article-processor.delegator';
import { Dictionary } from 'typescript-collections';
import { ArticleCompositionUtils } from '../../../util/article-composition-utils';

@Injectable({ providedIn: 'root' })
export class DeleteCodingArticleProcessor {
  private articleUsagesMarkedForDeletion: Dictionary<string, number> = null;

    constructor(
        private articleManager: ArticleManager,
        private projectModel: ProjectModel,
        private codingArticleUtils: CodingArticleUtils,
        private detachArticleUsageService: DetachArticleUsageService,
        private codingArticleProcessorDelegator: CodingArticleProcessorDelegator
    ) {}

    public async deleteCodingFingerIfNecessary(attachedUsage: ArticleUsageVO, composition: ArticleCompositionVO): Promise<void> {
      const attachedArticle: ArticleVO = this.articleManager.getCachedArticle(attachedUsage.articleId, attachedUsage.articleVersion);
      if (!attachedArticle) {
        return;
      }
      if (CodingArticleUtils.isCodingBolt(attachedArticle, attachedUsage)) {
        await this.deleteCodingFingerByCodingBolt(attachedUsage, composition);
      } else if (CodingArticleUtils.isCodingFinger(attachedArticle)) {
        // nothing to do here for now
      } else if (CodingArticleUtils.hasCodingFingerConnection(attachedArticle)) {
        await this.deleteCodingFingerByConnection(attachedUsage, composition);
      }
    }

    private async deleteCodingFingerByCodingBolt(codingBoltUsage: ArticleUsageVO, composition: ArticleCompositionVO): Promise<void> {
      const firstConnectionUsage = ArticleUsageUtils.tryGetFirstAsConnectionAreaUsage(codingBoltUsage);
      const secondConnectionUsage = ArticleUsageUtils.tryGetSecondAsConnectionAreaUsage(codingBoltUsage);
      const bridgeArticleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(firstConnectionUsage.connection.acceptingAreaId.articleUsageId);
      if (bridgeArticleUsage) {
        const bridgeArticleVO = this.articleManager.getCachedArticle(bridgeArticleUsage.articleId, bridgeArticleUsage.articleVersion);
        // find the connections on the bridgearticle that references the connections, which are in use by the bolt
        const connectionLeadingToCodingFinger: ConnectionAreaUsageVO = this.codingArticleUtils.searchCodingReferencesForConnectionLeadingToCodingFinger(
          bridgeArticleUsage,
          bridgeArticleVO,
          firstConnectionUsage.connection.acceptingAreaId.metaElementName,
          !secondConnectionUsage ? null : secondConnectionUsage.connection.acceptingAreaId.metaElementName
        );
        if (connectionLeadingToCodingFinger) {
          let position: number = CodingArticleUtils.getPositionFromConnectionName(connectionLeadingToCodingFinger.identity.metaElementName);
          if (!secondConnectionUsage) {
            position = CodingArticleUtils.getPositionFromConnectionName(firstConnectionUsage.connection.acceptingAreaId.metaElementName);
          }
          let connectedArticleUsageId: string = null;
          if (
            connectionLeadingToCodingFinger.connection.givingAreaIds &&
            connectionLeadingToCodingFinger.connection.givingAreaIds.length > 0 &&
            (connectionLeadingToCodingFinger.connection.givingAreaIds[0] as MetaElementUsageIdentityVO)
          ) {
            connectedArticleUsageId = (connectionLeadingToCodingFinger.connection.givingAreaIds[0] as MetaElementUsageIdentityVO).articleUsageId;
          }
          if (connectedArticleUsageId != null) {
            if (connectedArticleUsageId === bridgeArticleUsage.id) {
              // in this case the connection is a giving connection and we should check the id of the accepting connection
              connectedArticleUsageId = connectionLeadingToCodingFinger.connection.acceptingAreaId.articleUsageId;
            }
            const connectedArticleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(connectedArticleUsageId);
            if (connectedArticleUsage) {
              const connectedArticle: ArticleVO = this.articleManager.getCachedArticle(connectedArticleUsage.articleId, connectedArticleUsage.articleVersion);
              if (CodingArticleUtils.hasCodingFingerConnection(connectedArticle)) {
                await this.deleteCodingFinger(connectedArticleUsage, composition, position);
              } else {
                await this.recursiveDelete(connectedArticleUsage, composition, position);
              }
            }
          }
        }
      }
    }

    private async recursiveDelete(articleUsage: ArticleUsageVO, composition: ArticleCompositionVO, position: number): Promise<void> {
      if (!articleUsage || !composition) {
        return;
      }
      const conBackwardUsages: ArticleUsageVO[] = [];
      const cids: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleUsage);
      for (const bcid of cids) {
        const usage: ArticleUsageVO = this.projectModel.getArticleUsage(bcid);
        conBackwardUsages.push(usage);
      }
      for (const conBackwardUsage of conBackwardUsages) {
        const conBackwardArticle: ArticleVO = this.articleManager.getCachedArticle(conBackwardUsage.articleId, conBackwardUsage.articleVersion);
        if (CodingArticleUtils.hasCodingFingerConnection(conBackwardArticle)) {
          await this.deleteCodingFinger(conBackwardUsage, composition, position);
        } else {
          await this.recursiveDelete(conBackwardUsage, composition, position);
        }
      }
    }

    private async deleteCodingFingerByConnection(parentArticleUsage: ArticleUsageVO, composition: ArticleCompositionVO): Promise<void> {
      const connectedArticleUsages: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(parentArticleUsage);
      let connectedUsage: ArticleUsageVO = null;
      let connectedConnectionName: string = null;
      const connectedConnectionNames: string[] = [];
      if (ArrayUtils.isNotEmpty(connectedArticleUsages)) {
        connectedUsage = this.projectModel.getArticleUsage(connectedArticleUsages[0]);
        if (connectedUsage) {
          const connectedArticle: ArticleVO = this.articleManager.getCachedArticle(connectedUsage.articleId, connectedUsage.articleVersion);
          for (const connectionAreaUsage of ArticleUsageUtils.connectionAreaUsages(connectedUsage)) {
              for (const metaElementUsageIdentityVO of connectionAreaUsage.connection.givingAreaIds) {
                if (metaElementUsageIdentityVO.articleUsageId === parentArticleUsage.id) {
                  connectedConnectionName = connectionAreaUsage.identity.metaElementName;
                  connectedConnectionNames.push(connectedConnectionName);
                }
            }
          }
          while (connectedConnectionNames.length > 0) {
            connectedConnectionName = connectedConnectionNames.pop();
            if (connectedConnectionName) {
              let codingReference: string = CodingArticleUtils.getCodingReference(connectedArticle, connectedConnectionName);
              const codingReferences: string[] = CodingArticleUtils.getCodingReferences(connectedArticle, connectedConnectionName);
              codingReference = codingReferences.length > 0 ? codingReferences.pop() : null;
              if (codingReference) {
                const connectionAreaUsage = ArticleUsageUtils.findConnectionAreaUsageByName(connectedUsage, codingReference);
                if (
                  connectionAreaUsage &&
                  connectionAreaUsage.connection.givingAreaIds.length > 0
                ) {
                  if (codingReferences.length > 0) {
                    const codingReference2: string = codingReferences.pop();
                    const codingBoltId: string = (
                      connectionAreaUsage.connection.givingAreaIds[0] as MetaElementUsageIdentityVO
                    ).articleUsageId;
                    const connectionAreaUsage2 = ArticleUsageUtils.findConnectionAreaUsageByName(connectedUsage, codingReference2);
                    if (connectionAreaUsage2 && connectionAreaUsage2.identity.metaElementName === codingReference2
                      && connectionAreaUsage2.connection.givingAreaIds.length > 0) {
                      if (codingBoltId === (connectionAreaUsage2.connection
                        .givingAreaIds[0] as MetaElementUsageIdentityVO).articleUsageId) {
                          await this.deleteCodingFinger(parentArticleUsage, composition,
                          CodingArticleUtils.getPositionFromConnectionName(codingReference));
                      }
                    }
                  } else {
                    await this.deleteCodingFinger(parentArticleUsage, composition,
                      CodingArticleUtils.getPositionFromConnectionName(codingReference));
                  }
                }
              }
            }
          }
        }
      }
    }

    private async deleteCodingFinger(parentArticleUsage: ArticleUsageVO, composition: ArticleCompositionVO, position: number): Promise<void> {
      if (!parentArticleUsage || !composition) {
        return;
      }

      const codingFingerArticleUsageId: string = this.codingArticleProcessorDelegator.findCodingFingerUsageIdByParent(parentArticleUsage, position);
      if (!codingFingerArticleUsageId) {
        return;
      }
      const codingFingerArticleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(codingFingerArticleUsageId);
      if (codingFingerArticleUsage) {
        this.codingArticleUtils.storeRemovedCodingFingerArticleInformation(parentArticleUsage, codingFingerArticleUsage);
      }
      const articleUsage = ArticleCompositionUtils.findArticleUsageById(composition, codingFingerArticleUsageId);
      if(articleUsage) {
        await this.detachArticleUsageService.internalDetachArticleUsageFromProjectById(articleUsage.id, false);
        return;
      }

      this.markArticleUsageForDeletion(codingFingerArticleUsageId);
    }

    private markArticleUsageForDeletion(articleUsageId: string): void {
      if (!this.articleUsagesMarkedForDeletion) {
        this.articleUsagesMarkedForDeletion = new Dictionary<string, number>();
      }
      if (this.articleUsagesMarkedForDeletion.getValue(articleUsageId)) {
        let value: number = this.articleUsagesMarkedForDeletion.getValue(articleUsageId);
        this.articleUsagesMarkedForDeletion.setValue(articleUsageId, value++);
      } else {
        this.articleUsagesMarkedForDeletion.setValue(articleUsageId, 1);
      }
    }

    public isArticleUsageMarkedForDeletion(id: string): boolean {
      if (
        this.articleUsagesMarkedForDeletion &&
        this.articleUsagesMarkedForDeletion.getValue(id) &&
        this.articleUsagesMarkedForDeletion.getValue(id) > 0
      ) {
        return true;
      }
      return false;
    }

    public unMarkArticleUsageForDeletion(id: string): void {
      if (this.articleUsagesMarkedForDeletion) {
        this.articleUsagesMarkedForDeletion.setValue(id, -1);
      }
    }
}
